import { Form } from "@inertiajs/react"
import { Heart } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"

/**
 * OKS-964 — "Make this your Coup de cœur?" confirm (UX Spec §3).
 *
 * Inertia `<Form>` POST to the SERVER-SUPPLIED `action` — never a locally
 * constructed URL. The parent renders this component iff
 * `submission.coup_de_coeur_url` is non-null (the OKS-707 rule), so the URL is
 * always present here and the affordance and the transport cannot disagree
 * about who may mark what.
 *
 * Crew rules: no shadcn `Form*`, no react-hook-form, no zod. `AlertDialog` is
 * the shipped Base UI primitive — hand-ported; the shadcn generator is banned
 * (it emits Radix). Phrased without naming the command, because
 * `no_shadcn_add_prescriptions_spec` matches the literal string and cannot tell
 * a prohibition from an instruction — a warning against it reads to the guard
 * exactly like a recommendation of it.
 *
 * # ⚠️ THE FINALITY IS THE WHOLE POINT OF THIS DIALOG
 *
 * The pick is IMMOVABLE (PRD D2): no unmark, no move, no revocation. This
 * dialog is the only mitigation for that, so the body states the finality
 * plainly rather than softening it. If the copy is ever trimmed, the mitigation
 * goes with it.
 *
 * # ⚠️ DEFAULT FOCUS IS CANCEL, NOT THE CONFIRM
 *
 * `masterclass-publish-confirm-dialog.tsx` is the shape this copies, and it
 * `autoFocus`es its primary action deliberately — operator speed on a
 * REVERSIBLE publish, with delete named as the Cancel-by-default
 * counter-example. Coup de cœur is irreversible, so it takes the delete
 * posture (UX Spec §6). Copy the primitive shape from that file, NOT its focus
 * default — that is exactly the line a copy-paste keeps for free.
 *
 * The confirm is NOT `variant="destructive"`: this is a positive act, and the
 * red would misread it as damage rather than as finality.
 */
interface Props {
  /** Server-supplied POST target (`submission.coup_de_coeur_url`). */
  action: string
  submissionId: number
  /** Creator handle for the title/body/aria, without the leading `@`. */
  creatorHandle: string | null
}

export function CoupDeCoeurConfirmDialog({
  action,
  submissionId,
  creatorHandle,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const handle = creatorHandle ?? "—"

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={t("submissions.brand.coup_de_coeur.button_aria", {
              handle,
            })}
            data-testid={`submission-coup-de-coeur-${submissionId}`}
          >
            <Heart aria-hidden="true" className="mr-1 size-4" />
            {t("submissions.brand.coup_de_coeur.button")}
          </Button>
        }
      />
      <AlertDialogContent data-testid="coup-de-coeur-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("submissions.brand.confirm.coup_de_coeur.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("submissions.brand.confirm.coup_de_coeur.body", { handle })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form
          action={action}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <AlertDialogFooter>
              {/* Cancel first in DOM order AND autoFocus'd — see the class
                  doc-comment. Reuses the shipped `confirm.cancel` string; there
                  is deliberately no second `confirm.coup_de_coeur.cancel`. */}
              <Button
                type="button"
                variant="outline"
                autoFocus
                onClick={() => setOpen(false)}
                data-testid="coup-de-coeur-cancel"
              >
                {t("submissions.brand.confirm.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={processing}
                data-testid="coup-de-coeur-confirm"
              >
                {t("submissions.brand.confirm.coup_de_coeur.cta")}
              </Button>
            </AlertDialogFooter>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
